import { useAppSelector } from 'appState/store'
import DoNotRepeat from 'assets/images/icons/DoNotRepeat.svg'
import { getCustomerBatchCount } from 'features/autocheck/autoCheckApi'
import { BatchType, IBatchSettings } from 'features/autocheck/types'
import { formatBatchType } from 'features/autocheck/utils'
import { BatchValidationErrors, getBatchErrors } from 'features/autocheck/utils/BatchErrors'
import { IFileUploadError } from 'features/claims/types'
import { settingsServiceApiPaths, setUserPreference } from 'features/settings/settingsApi'
import { useCustomerPreference } from 'features/settings/settingsHooks'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { SettingsRequestTypeEnum } from 'features/settings/types/settingsEnum'
import { useEffect, useRef, useState } from 'react'
import { FaAngleDown, FaBuilding, FaFileCsv, FaRetweet } from 'react-icons/fa'
import { Badge, Button, DownloadDropdown, Tooltip, ObModal } from '@outbound-ai/ursa'
import { ObStepper } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { PositionEnum } from 'types/cssTypes'
import { longTextToSmall } from 'utils/helper'

import { AllClaimFailedMessage } from '../internal/AllClaimFailedMessage'
import { ClaimFileExclusions } from './ClaimFileExclusions'
import { ClaimFileSubmit } from './ClaimFileSubmit'
import { ClaimFileUpload } from './ClaimFileUpload'
import { ClaimFileValidate } from './ClaimFileValidate'
import { ClaimProcessDiscard } from './ClaimProcessDiscard'
import { DISCARD_BUTTON, IGNORE_BUTTON, STEPPER_TITLE, UploadStepper } from './Constants'
import { BatchTooltipText } from 'features/autocheck/utils/tootlTipText'

export const BatchUploadClaim = () => {
  const { currentTenant } = useAppSelector((state) => state.auth)
  const [batchType, setBatchType] = useState<BatchType>()
  const [activeStep, setActiveStep] = useState(UploadStepper.selectFile)
  const [showModal, setShowModal] = useState(false)
  const [responseData, setResponseData] = useState({ batchId: '' })
  const [fileError, setFileError] = useState<IFileUploadError | null>(null)
  const [claimFileName, setClaimFileName] = useState<string | null>(null)
  const [showDiscard, setShowDiscard] = useState(false)
  const [batchErrors, setBatchErrors] = useState<BatchValidationErrors | null>(null)
  const [repeatCalls, setRepeatCalls] = useState(false)
  const [isUploadDisabled, setUploadDisabled] = useState(false)
  const [tenantAutoCheck, setTenantAutoCheck] = useState({
    AutoCheck: SettingsRequestTypeEnum.always,
    EOB: SettingsRequestTypeEnum.always,
    AutoCheckCalls: SettingsRequestTypeEnum.always,
  })
  const [isPocCustomer, setPocCustomer] = useState(false)
  const { currentCustomer, eobEnabled, eobFaxNumber, ascEnabled } = useAppSelector((state) => state.auth)

  const isModalClosed = useRef(false) // This is mainly used for handling setTimeout from ClaimValidate component

  const { data: tenantAutoCheckData } = useCustomerPreference(UserPreferencesKeysEnum.AutoCheckPreferences)

  const batchSetting = () => {
    getCustomerBatchCount().then((res: IBatchSettings) => {
      const { maxBatchLimitForPOCCustomer, totalBatchesSubmittedByCustomer, maxClaimsPerBatchLimitForCustomer } = res
      const checkPocCustomer =
        maxClaimsPerBatchLimitForCustomer && maxClaimsPerBatchLimitForCustomer >= 0 ? true : false
      setPocCustomer(checkPocCustomer)
      if (maxBatchLimitForPOCCustomer && totalBatchesSubmittedByCustomer) {
        const batchLimit = isBatchLimitExceeded(maxBatchLimitForPOCCustomer, totalBatchesSubmittedByCustomer)
        setUploadDisabled(batchLimit)
      }
    })
  }

  useEffect(() => {
    batchSetting()
  }, [])

  useEffect(() => {
    if (responseData?.batchId || fileError) {
      setActiveStep(UploadStepper.validate)
    }
  }, [responseData, fileError])

  useEffect(() => {
    if (tenantAutoCheckData?.value) {
      const autoCheck = JSON.parse(tenantAutoCheckData?.value)
      setTenantAutoCheck(autoCheck)
    }
  }, [tenantAutoCheckData])

  useEffect(() => {
    if (tenantAutoCheckData && !tenantAutoCheckData?.value) {
      setUserPreference(settingsServiceApiPaths.customerPreferencesPut(), {
        key: UserPreferencesKeysEnum.AutoCheckPreferences,
        value: JSON.stringify(tenantAutoCheck),
        customerId: currentCustomer,
      })
    }
  }, [tenantAutoCheck, tenantAutoCheckData])

  function initialSetup() {
    setBatchErrors(null)
    setShowDiscard(false)
    setActiveStep(UploadStepper.selectFile)
  }

  const setType = (type: BatchType) => {
    if (
      type === BatchType.Claim_Autocheck &&
      tenantAutoCheck.EOB === 'always' &&
      eobEnabled &&
      (eobFaxNumber || eobFaxNumber !== '')
    ) {
      setBatchType(BatchType.Claim_Autocheck_EOB)
    } else if (type === BatchType.Claim_Autocheck) {
      setBatchType(BatchType.Claim_Autocheck)
    } else if (type === BatchType.Claim_Import) {
      setBatchType(BatchType.Claim_Import)
    } else if (type === BatchType.Claim_ASC) {
      setBatchType(BatchType.Claim_ASC)
    }
    initialSetup()
    setClaimFileName(null)
    isModalClosed.current = true
    setShowModal(true)
  }

  const handleCloseModal = () => {
    const showDiscard = activeStep === 1 || activeStep === 2 || activeStep === 3
    if (showDiscard && !fileError) {
      setShowDiscard(true)
    } else {
      isModalClosed.current = false
      setBatchErrors(null)
      setShowDiscard(false)
      setShowModal(false)
      setActiveStep(UploadStepper.selectFile)
    }
  }

  const discardModal = (value: string) => {
    if (value === DISCARD_BUTTON) {
      isModalClosed.current = false
      setActiveStep(UploadStepper.selectFile)
      setBatchErrors(null)
      setShowModal(false)
      setRepeatCalls(false)
    }
    if (value === IGNORE_BUTTON) {
      setShowModal(true)
    }

    setShowDiscard(false)
  }

  const handleExclusion = (activeStep: number) => {
    if (activeStep === UploadStepper.exclusions && !batchErrors) {
      getBatchErrors(responseData.batchId, batchType!).then((batchErrors) => {
        setBatchErrors(batchErrors)
        if (batchErrors.totalInvalidForImport === 0) {
          setActiveStep(UploadStepper.submit)
        } else {
          setActiveStep(activeStep)
        }
      })
    } else {
      setActiveStep(activeStep)
    }
  }
  const handleStartOver = () => {
    setBatchErrors(null)
    setFileError(null)
    setActiveStep(0)
    setClaimFileName(null)

    if (batchType === BatchType.Claim_Autocheck_EOB) {
      setBatchType(BatchType.Claim_Autocheck)
    }
  }

  function getSelectedStepperComponent() {
    switch (activeStep) {
      case 0:
        return (
          <ClaimFileUpload
            batchType={batchType!}
            onSetClaimData={(data: any) => setResponseData(data)}
            setFileError={(error: IFileUploadError | null) => setFileError(error)}
            setFileName={(fileName: string | null) => setClaimFileName(fileName)}
            repeatCalls={(repeat: boolean) => setRepeatCalls(repeat)}
            eobSelected={(eob: boolean) => {
              if (eob) {
                setBatchType(BatchType.Claim_Autocheck_EOB)
              } else {
                setBatchType(BatchType.Claim_Autocheck)
              }
            }}
            isPocCustomer={isPocCustomer}
          />
        )
      case 1:
        return fileError ? (
          <AllClaimFailedMessage fileError={fileError} startOver={() => handleStartOver()} />
        ) : (
          <ClaimFileValidate
            activeStep={(val: number) => handleExclusion(val)}
            batchId={responseData?.batchId}
            clearValidateTimeOut={(e: any) => {
              if (!isModalClosed.current) {
                window.clearTimeout(e)
              }
            }}
            startOver={() => handleStartOver()}
          />
        )
      case 2:
        return (
          <ClaimFileExclusions
            activeStep={(val: number) => setActiveStep(val)}
            batchErrors={batchErrors!}
            batchType={batchType!}
            batchId={responseData?.batchId}
            startOver={() => handleStartOver()}
          />
        )
      case 3:
        return (
          <ClaimFileSubmit
            activeStep={(val: number) => {
              if (val === UploadStepper.closeModel) {
                batchSetting()
                setShowDiscard(false)
                setShowModal(false)
                isModalClosed.current = false
                setActiveStep(UploadStepper.selectFile)
                return
              }
              setActiveStep(val)
            }}
            batchId={responseData.batchId}
            batchType={batchType!}
            batchErrors={batchErrors!}
            fileName={claimFileName!}
          />
        )
    }
  }

  const titleWithSub = () => {
    return (
      <TitleContainer repeatCall={repeatCalls}>
        <h2>Upload Claim File</h2>
        <div className="sub-container">
          {formatBatchType(JSON.stringify(batchType!))}
          {batchType !== BatchType.Claim_Import && (
            <div className="repeated-call">
              <Tooltip
                tooltipText={<>{repeatCalls ? BatchTooltipText.repeatCallsOn : BatchTooltipText.repeatCallOff}</>}
                tooltipStyles={{ 'min-width': '15rem', transform: 'translateY(45%) translateX(-27%)' }}
                position={PositionEnum.bottom}
                positionTooltipArrow={10}
              >
                {repeatCalls ? (
                  <Badge type="info" fill="light" variant="tag" icon={<FaRetweet />}>
                    Repeat Calls On
                  </Badge>
                ) : (
                  <Badge type="grey" fill="dark" variant="tag" icon={<img src={DoNotRepeat} />}>
                    Repeat Calls Off
                  </Badge>
                )}
              </Tooltip>
            </div>
          )}

          {claimFileName && (
            <div className="file-name">
              <FaFileCsv className="csv-icon" /> {longTextToSmall(claimFileName, 22, 50)}
            </div>
          )}

          <div className="tenant-container">
            <FaBuilding className="tenant-icon" /> {currentTenant && longTextToSmall(currentTenant, 9, 18)}
          </div>
        </div>
      </TitleContainer>
    )
  }

  return (
    <Div>
      {tenantAutoCheck?.AutoCheck === 'always' ? (
        <Tooltip
          tooltipText={
            isUploadDisabled
              ? 'The maximum number of batches has been uploaded for AutoCheck for your organization. Please contact your Customer Success Representative for more information about your Claims Work trial period.'
              : ''
          }
          position={PositionEnum.right}
          positionTooltipArrow={20}
          tooltipStyles={{
            minWidth: '15rem',
            transform: 'translateY(-13%) translateX(30%)',
          }}
        >
          <Button
            className="upload-file-btn"
            radius="rounded"
            variant="info"
            onClick={() => setType(BatchType.Claim_Autocheck)}
            disabled={isUploadDisabled}
          >
            Upload File
          </Button>
        </Tooltip>
      ) : (
        <DownloadDropdown
          buttonName={
            <>
              {' '}
              Upload File <FaAngleDown className="down-arrow" />
            </>
          }
          variant="info"
          items={[
            {
              items: [
                {
                  text: 'Upload for AutoCheck',
                  onClick: () => setType(BatchType.Claim_Autocheck),
                  disabled: isUploadDisabled,
                  tooltip: isUploadDisabled
                    ? 'The maximum number of batches has been uploaded for AutoCheck for your organization. Please contact your Customer Success Representative for more information about your Claims Work trial period.'
                    : '',
                },
                {
                  text: 'Upload Appeal Status Check',
                  onClick: () => setType(BatchType.Claim_ASC),
                  disabled: !ascEnabled,
                },
                {
                  text: 'Upload for Import Only',
                  onClick: () => setType(BatchType.Claim_Import),
                },
              ],
            },
          ]}
        />
      )}

      <ObModal
        show={showModal}
        close={handleCloseModal}
        title={titleWithSub()}
        customSize="large"
        size="lg"
        height={showDiscard ? '100%' : 'auto'}
        dataTestId="BatchUploadClaimTestId"
      >
        {showModal && (
          <StepperContainer>
            <ObStepper steps={STEPPER_TITLE} activeStep={activeStep} />
            {showDiscard && <ClaimProcessDiscard selectBtn={(value) => discardModal(value)} />}
            {getSelectedStepperComponent()}
          </StepperContainer>
        )}
      </ObModal>
    </Div>
  )
}

const Div = styled.div`
  .down-arrow {
    width: 1rem !important;
    height: 1rem !important;
    margin-bottom: 0 !important;
    color: ${({ theme }) => theme.colors.neutral.lightest} !important;
  }
  .upload-file-btn {
    height: 2rem;
    font-size: 0.875rem;
    font-weight: 600;
  }
`

const StepperContainer = styled.div`
  border: solid 1px ${({ theme }) => theme.colors.grey.lighter};
  border-radius: 8px;
`
const TitleContainer = styled.div<{ repeatCall: boolean }>`
  h2 {
    margin-bottom: 0.25rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary.neutral};
  }
  .do-not-repeat-icon {
    width: 24px;
    height: 24px;
    flex-grow: 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    margin-left: 12px;
    padding: 4px;
    border-radius: 4px;
    color: ${({ theme }) => theme.colors.warning.neutral};
    border: solid 1px
      ${({ theme, repeatCall }) => (repeatCall ? theme.colors.warning.lighter : theme.colors.grey.lighter)};
    background-color: ${({ theme, repeatCall }) =>
      repeatCall ? theme.colors.warning.lightest : theme.colors.grey.lightest};

    .retweet-icon {
      font-size: 0.875rem;
    }
  }
  .sub-container {
    display: flex;
    align-items: center;
    .file-name {
      margin-left: 0.75rem;
    }
    .file-name,
    .tenant-container {
      display: flex;
      align-items: center;
      font-size: 0.875rem;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.14;
      letter-spacing: normal;
      text-align: left;
      color: ${({ theme }) => theme.colors.grey.dark};
    }
    .tenant-container {
      margin-left: 0.75rem;
    }
    .csv-icon,
    .tenant-icon {
      font-size: 0.75rem;
      margin-right: 0.125rem;
      margin-bottom: 0.125rem;
    }
    .repeated-call {
      display: flex;
      align-items: center;
      margin-left: 0.75rem;
      .retweet-icon {
        color: ${({ theme }) => theme.colors.primary.neutral};
      }
      img {
        /* update image color with filter it should be dark grey */
        filter: invert(0.5);
      }
      .tooltip-children-container {
        display: flex;
      }
    }
  }
`

function isBatchLimitExceeded(limit: number, totalBatches: number) {
  return totalBatches >= limit
}
